package com.advaita.cloud.drive.ui.store

import com.advaita.cloud.drive.shared.AccessGrantDto
import com.advaita.cloud.drive.shared.PrincipalRef
import com.advaita.cloud.drive.shared.SourceStatus
import com.advaita.cloud.drive.ui.components.ShareCap
import com.advaita.cloud.drive.ui.components.capForGrant
import com.advaita.cloud.drive.ui.components.capSentence

/** What the remove control of a "Who has access" row does. */
enum class RemoveAction { REMOVE, LEAVE }

/** The words for a folder above the file whose name the caller may not read. */
const val FOLDER_ABOVE = "a folder above this one"

/**
 * The confirm, the button and the toast of one Leave (coordinator decision (c)): Leave asks for
 * confirmation everywhere, and a grant that reaches the item through a folder names the folder,
 * because Leave removes the whole folder's share. [name] is the item or that folder; null (the
 * backend hides a folder the caller cannot read) says [FOLDER_ABOVE].
 */
data class LeavePrompt(val button: String, val title: String, val message: String, val done: String)

fun leavePrompt(name: String?): LeavePrompt {
    val quoted = name?.let { "\"$it\"" } ?: FOLDER_ABOVE
    return LeavePrompt(
        button = "Leave $quoted",
        title = "Leave $quoted?",
        message = "It leaves Shared with me. The person who shared it can share it with you again.",
        done = "You left $quoted.",
    )
}

/** The toast after Remove, which has no confirm step (the frontend build ruling, item 3). */
fun removedToast(kind: String, ref: PrincipalRef, namesStatus: String?): String {
    val name = ref.name?.takeIf { it.isNotBlank() }
    return when {
        kind == "ORG" -> "Removed your organization's access."
        name != null -> "Removed $name's access."
        kind != "TEAM" && namesStatus == SourceStatus.OK -> "Removed a former member's access."
        else -> "Removed the share."
    }
}

/**
 * The line under a grant's name: who shared it. A sharer az2 no longer lists is "a former
 * member". With the people source down, the name is not known, so there is no line.
 */
fun sharedByLine(ref: PrincipalRef, peopleStatus: String?): String? {
    val name = ref.name?.takeIf { it.isNotBlank() }
    return when {
        name != null -> "Shared by $name"
        peopleStatus == SourceStatus.OK -> "Shared by a former member"
        else -> null
    }
}

/**
 * The options of a level control: the route's [levels] (S6), and first the grant's [current]
 * level when the route does not offer it (a custom grant, or a level above the caller's own).
 * The renderer shows that extra option disabled, so the control never misstates the grant.
 */
fun levelOptionsFor(current: String, levels: List<String>): List<String> =
    if (current.isNotEmpty() && current !in levels) listOf(current) + levels else levels

/**
 * The controls of one "Who has access" row, as the flags drive them.
 *
 * @property levelEnabled false disables the level control (`relevelable` false, S6).
 * @property removeAction null hides the remove control (`removable` false). The caller's own
 *   USER grant is always [RemoveAction.LEAVE], whatever the flags say (S6).
 * @property removeLabel the remove control's words: "Remove", "Leave", or `Leave "F"` for a
 *   grant through a folder.
 * @property leave the Leave confirm, for [RemoveAction.LEAVE].
 * @property cap the S8 cap on the row, [ShareCap.NOT_ON_CHAIN] or one from a refused write.
 * @property sentence the one sentence beside the disabled control, or null.
 */
data class AccessRowControls(
    val name: String,
    val detail: String?,
    val levelEnabled: Boolean,
    val levelOptions: List<String>,
    val removeAction: RemoveAction?,
    val removeLabel: String?,
    val leave: LeavePrompt?,
    val cap: ShareCap?,
    val sentence: String?,
)

/**
 * The controls for [g], for a caller who may give [levels], on the item [itemName].
 *
 * A grant that reaches the file through a folder above it ([AccessGrantDto.inheritedFrom]) is
 * read-only here (the frontend build ruling, item 2): the vault's flags are the folder grant's,
 * and a change from a file's dialog would change the whole folder. The row says where to change
 * it. The caller's own grant keeps Leave even then, and that Leave names the folder.
 * [ownerName] is the owner's own name, null when az2 did not give one, so row 8 then says
 * "the owner" and never "Only Former member". [writeCap] is a cap a refused write put on the row.
 */
fun accessRowControls(
    g: AccessGrantDto,
    levels: List<String>,
    ownerName: String?,
    peopleStatus: String?,
    teamsStatus: String?,
    writeCap: ShareCap? = null,
    itemName: String? = null,
): AccessRowControls {
    val namesStatus = if (g.principalKind == "TEAM") teamsStatus else peopleStatus
    val inherited = g.inheritedFrom
    val chainRefused = writeCap == ShareCap.NOT_ON_CHAIN
    val cap = if (chainRefused) ShareCap.NOT_ON_CHAIN else capForGrant(g) ?: writeCap
    val levelLocked = inherited != null || chainRefused || !g.relevelable
    val removeLocked = inherited != null || chainRefused || !g.removable
    val sentence = when {
        inherited != null -> {
            val folder = inherited.name?.let { "the folder \"$it\"" } ?: FOLDER_ABOVE
            "Shared through $folder. Change it on that folder."
        }
        cap != null -> capSentence(cap, owner = ownerName)
        else -> null
    }
    val action = when {
        g.self -> RemoveAction.LEAVE
        !removeLocked -> RemoveAction.REMOVE
        else -> null
    }
    val leave = if (action == RemoveAction.LEAVE) leavePrompt(if (inherited != null) inherited.name else itemName) else null
    return AccessRowControls(
        name = principalLabel(g.principalKind, g.principal, namesStatus),
        detail = sharedByLine(g.sharedBy, peopleStatus),
        levelEnabled = !levelLocked,
        levelOptions = levelOptionsFor(g.level, levels),
        removeAction = action,
        removeLabel = when (action) {
            RemoveAction.REMOVE -> "Remove"
            RemoveAction.LEAVE -> if (inherited != null) leave!!.button else "Leave"
            null -> null
        },
        leave = leave,
        cap = if (inherited != null) null else cap,
        sentence = sentence,
    )
}
